/** Texts of manifest, permissions, audit and workflow. Pages have their own files next to this one. */
export const base = {
  name: 'Projekte',
  description:
    'Projekte mit Auftraggeber, Objekt, Team, Beteiligten, Leistungen und Phasen; Objekte als Gebäude mit ihrer Geschichte.',
  nav: { projects: 'Projekte', objects: 'Objekte', types: 'Projektarten und Phasen' },
  entities: { project: 'Projekt', object: 'Objekt' },
  permissions: {
    project: {
      read: {
        label: 'Projekte ansehen',
        description: 'Projekte öffnen, mit Bereich „Team“ nur Projekte im eigenen Team.',
      },
      write: {
        label: 'Projekte bearbeiten',
        description:
          'Projekte anlegen und ändern, Team, Beteiligte, Leistungen und Phasen pflegen. Mit Bereich „Team“ nur Projekte im eigenen Team.',
      },
      comment: {
        label: 'Projekte kommentieren',
        description: 'Kommentare an Projekten schreiben.',
      },
      remind: {
        label: 'Wiedervorlagen an Projekten',
        description: 'Eigene Wiedervorlagen an Projekten setzen.',
      },
      export: {
        label: 'Projekte exportieren',
        description: 'Die Liste der Projekte als CSV-Datei herunterladen.',
      },
      archive: {
        label: 'Projekte archivieren',
        description: 'Projekte archivieren und zurückholen, Projekte löschen.',
      },
      share: {
        label: 'Projekte freigeben',
        description: 'Projekte anderen Firmen der Gruppe zum Lesen freigeben.',
      },
    },
    object: {
      read: { label: 'Objekte ansehen', description: 'Gebäude und Liegenschaften öffnen.' },
      write: { label: 'Objekte bearbeiten', description: 'Objekte anlegen und ändern.' },
      comment: { label: 'Objekte kommentieren', description: 'Kommentare an Objekten schreiben.' },
      export: {
        label: 'Objekte exportieren',
        description: 'Die Liste der Objekte als CSV-Datei herunterladen.',
      },
      archive: {
        label: 'Objekte archivieren',
        description: 'Objekte archivieren und zurückholen, Objekte löschen.',
      },
      plan: {
        label: 'Pläne pflegen',
        description:
          'Grundrisse und Lagepläne an Objekten anlegen, ändern und löschen, an Projekten mit Schreibzugriff auf das Projekt.',
      },
    },
    settings: {
      manage: {
        label: 'Projekte einrichten',
        description: 'Projektarten, Phasenvorlagen und Nummernkreise der Firma pflegen.',
      },
    },
  },
  roles: {
    reader: 'Projekte: Lesen',
    editor: 'Projekte: Bearbeiten',
    manager: 'Projekte: Verwalten',
  },
  states: {
    acquisition: 'Akquise',
    ordered: 'Beauftragt',
    active: 'In Bearbeitung',
    paused: 'Ruht',
    closed: 'Abgeschlossen',
    cancelled: 'Abgebrochen',
  },
  transitions: {
    order: 'Beauftragt',
    start: 'Bearbeitung beginnen',
    pause: 'Ruhen lassen',
    close: 'Abschließen',
    cancel: 'Abbrechen',
  },
  sequences: { project: 'Projektnummer', object: 'Objektnummer' },
  sections: {
    customerProjects: 'Projekte',
    customerObjects: 'Objekte',
  },
  audit: {
    project: {
      create: 'Projekt angelegt',
      update: 'Projekt geändert',
      archive: 'Projekt archiviert',
      restore: 'Projekt zurückgeholt',
      delete: 'Projekt gelöscht',
      anonymize: 'Projekt anonymisiert',
    },
    member: {
      add: 'Person ins Projektteam aufgenommen',
      update: 'Rolle im Projektteam geändert',
      remove: 'Person aus dem Projektteam entfernt',
    },
    distribution: {
      create: 'Verteiler angelegt',
      update: 'Verteiler umbenannt',
      delete: 'Verteiler gelöscht',
      add: 'Empfänger in Verteiler aufgenommen',
      remove: 'Empfänger aus Verteiler entfernt',
    },
    participant: {
      add: 'Beteiligten hinzugefügt',
      update: 'Beteiligten geändert',
      remove: 'Beteiligten entfernt',
    },
    service: {
      create: 'Leistung angelegt',
      update: 'Leistung geändert',
      delete: 'Leistung gelöscht',
    },
    phase: {
      create: 'Phase angelegt',
      update: 'Phase geändert',
      delete: 'Phase gelöscht',
    },
    object: {
      create: 'Objekt angelegt',
      update: 'Objekt geändert',
      archive: 'Objekt archiviert',
      restore: 'Objekt zurückgeholt',
      delete: 'Objekt gelöscht',
      anonymize: 'Objekt anonymisiert',
      link: 'Objekt zum Projekt hinzugefügt',
      unlink: 'Objekt vom Projekt entfernt',
    },
    type: {
      create: 'Projektart angelegt',
      update: 'Projektart geändert',
      archive: 'Projektart archiviert',
      restore: 'Projektart zurückgeholt',
    },
    template: {
      create: 'Phasenvorlage angelegt',
      update: 'Phasenvorlage geändert',
      archive: 'Phasenvorlage archiviert',
      restore: 'Phasenvorlage zurückgeholt',
    },
  },
  guides: {
    projects: {
      title: 'Projekte',
      summary: 'Projekte anlegen, Team, Beteiligte, Leistungen und Phasen pflegen.',
    },
    objects: {
      title: 'Objekte',
      summary: 'Gebäude anlegen und ihre Projekte über die Jahre sehen.',
    },
    types: {
      title: 'Projektarten und Phasen',
      summary: 'Projektarten und Phasenvorlagen der Firma einrichten.',
    },
  },
  errors: {
    address: 'Die Angaben zur Adresse sind zu lang.',
    usage: 'Die Nutzung darf höchstens 100 Zeichen lang sein.',
    notes: 'Die Notizen dürfen höchstens 4000 Zeichen lang sein.',
    specialBuilding: 'Bitte angeben, ob es ein Sonderbau ist.',
    georeferenced: 'Bitte angeben, ob Mängel per GPS verortet werden.',
    title: 'Bitte eine Bezeichnung eingeben.',
    description: 'Die Beschreibung darf höchstens 4000 Zeichen lang sein.',
    type: 'Bitte eine aktive Projektart wählen.',
    object: 'Das Objekt gibt es nicht oder es ist archiviert.',
    objectNotUsed: 'Projekte dieser Art haben kein Objekt.',
    objectLinked: 'Das Objekt gehört schon zum Projekt.',
    objectsLinked:
      'Das Projekt hat Objekte. Erst die Objekte entfernen, dann eine Projektart ohne Objekt wählen.',
    tooManyObjects: 'Ein Projekt hat höchstens 500 Objekte.',
    project: 'Das Projekt gibt es nicht, oder du darfst es nicht ändern.',
    client: 'Bitte einen Auftraggeber wählen, der in Kunden verfügbar ist.',
    clientContact: 'Der Ansprechpartner muss zum Auftraggeber gehören.',
    date: 'Bitte ein gültiges Datum eingeben.',
    endsOn: 'Das Ende darf nicht vor dem Beginn liegen.',
    anonymized: 'Das Projekt ist anonymisiert und lässt sich nicht mehr ändern.',
    account: 'Bitte ein aktives Konto der Firma wählen.',
    memberRole: 'Bitte eine Rolle wählen.',
    memberExists: 'Die Person ist schon im Team.',
    teamFull: 'Ein Team hat höchstens 100 Personen.',
    leadTaken: 'Das Projekt hat schon eine Projektleitung.',
    organization: 'Bitte eine Organisation wählen, die in Kunden verfügbar ist.',
    contact: 'Der Ansprechpartner muss zur Organisation gehören.',
    participantRole: 'Bitte eine Rolle wählen.',
    distributionName: 'Bitte einen Namen für den Verteiler angeben (höchstens 100 Zeichen).',
    distributionExists: 'Einen Verteiler mit diesem Namen gibt es im Projekt schon.',
    distributionsFull: 'Ein Projekt hat höchstens 50 Verteiler.',
    distributionFull: 'Ein Verteiler fasst höchstens 500 Empfänger.',
    distributionContact: 'Ein gewählter Ansprechpartner ist nicht mehr verfügbar.',
    email: 'Eine E-Mail-Adresse ist ungültig.',
    recipients: 'Bitte mindestens einen Empfänger angeben.',
    note: 'Die Notiz darf höchstens 500 Zeichen lang sein.',
    nameTaken: 'Diesen Namen gibt es schon.',
    defaultsExist: 'Die Firma hat schon Projektarten.',
    templateMissing: 'Die Phasenvorlage gibt es nicht oder sie ist archiviert.',
    noServices: 'Projekte dieser Art haben keine Leistungen.',
    name: 'Bitte einen Namen angeben (höchstens 200 Zeichen).',
    phaseCode: 'Das Kürzel darf höchstens 20 Zeichen lang sein.',
    phases: 'Eine Vorlage braucht 1 bis 30 Phasen.',
    projectArchived: 'Das Projekt ist archiviert. Hol es zurück, um es zu ändern.',
    objectName: 'Bitte einen Namen für das Objekt angeben (höchstens 200 Zeichen).',
    customerNumber: 'Die Objektnummer des Kunden darf höchstens 50 Zeichen lang sein.',
    countryCode: 'Bitte ein Land als zweistelligen Code angeben, z. B. DE.',
    coordinates: 'Breiten- und Längengrad gehören zusammen.',
    buildingClass: 'Die Gebäudeklasse liegt zwischen 1 und 5.',
    objectOrganization: 'Eigentümer oder Betreiber ist nicht verfügbar oder wurde zusammengeführt.',
    objectInUse:
      'Das Objekt gehört noch zu Projekten und lässt sich nicht löschen. Es lässt sich archivieren.',
    objectAnonymized: 'Das Objekt ist anonymisiert und lässt sich nicht mehr ändern.',
    state: 'Bitte ein Bundesland aus der Liste wählen.',
  },
  offline: { projects: 'Projekte', objects: 'Objekte' },
};
